import { useEffect, useRef, useState } from "react";
import type { ShipVo, TransportOrderDetailsShip } from "@zhihaoscm/entity";
import { ZhMap, type ShipInfoVo, type ShipTrack, type ZhMapRef } from "zhihao-ui-react";

import { deviceService } from "@/api/services/deviceService";
import { shipService } from "@/api/services/shipService";
import { CDN_URL } from "@/config";

export interface MapVideoWrapProps {
    vo?: ShipVo;
    transportOrderDetailsShip?: TransportOrderDetailsShip;
}

/** 将接口轨迹点转换为地图组件可识别的经纬度数据，过滤无效坐标。 */
function normalizeTrackPoints(shipId: string, list: unknown[]): ShipTrack[] {
    return list.flatMap((item, index) => {
        const point = item as Record<string, unknown>;
        const lon = Number(point.lon ?? point.lng ?? point.longitude);
        const lat = Number(point.lat ?? point.latitude);
        return Number.isFinite(lon) && Number.isFinite(lat)
            ? [{ ...point, id: String(point.id ?? `${shipId}-${index}`), lon, lat } as ShipTrack]
            : [];
    });
}

/** 提货详情船舶地图；严格复用 Vue 的船舶定位、选中和历史/实时轨迹加载顺序。 */
export default function MapVideoWrap({ vo, transportOrderDetailsShip }: MapVideoWrapProps) {
    const mapRef = useRef<ZhMapRef | null>(null);
    const [selectedShipInfo, setSelectedShipInfo] = useState<ShipInfoVo>();
    const [trackList, setTrackList] = useState<ShipTrack[]>([]);
    const [captureUrl, setCaptureUrl] = useState("");
    const shipId = vo?.ship?.id ? String(vo.ship.id) : "";
    const firstDevice = vo?.devices?.[0];

    useEffect(() => {
        let active = true;
        if (!firstDevice?.serialNo) {
            setCaptureUrl("");
            return () => {
                active = false;
            };
        }
        /** 与 Vue 一致，地图左下角展示船舶第一路设备的最新抓拍。 */
        void deviceService
            .getCapture(firstDevice.serialNo)
            .then(url => {
                if (active) setCaptureUrl(url || "");
            })
            .catch(() => {
                if (active) setCaptureUrl("");
            });
        return () => {
            active = false;
        };
    }, [firstDevice?.serialNo]);

    useEffect(() => {
        let active = true;
        if (!shipId) {
            setSelectedShipInfo(undefined);
            setTrackList([]);
            return () => {
                active = false;
            };
        }

        /** 船舶定位与轨迹相互独立，单个接口失败时仍保留可用的地图内容。 */
        const loadMapData = async () => {
            try {
                const shipInfo = await shipService.findByIdAndCreate(shipId);
                if (active) setSelectedShipInfo(shipInfo as ShipInfoVo);
            } catch {
                if (active) setSelectedShipInfo(undefined);
            }

            const start =
                transportOrderDetailsShip?.deliveryInfo?.loadingDate ||
                transportOrderDetailsShip?.lazbInfo?.loadingCompletionTime ||
                transportOrderDetailsShip?.ownerSetSailTime;
            if (!start) {
                if (active) setTrackList([]);
                return;
            }
            try {
                const list = await shipService.aisTrack({
                    mmsi: shipId,
                    start,
                    end: transportOrderDetailsShip?.captainArrivalTime || Date.now()
                });
                if (active) setTrackList(normalizeTrackPoints(shipId, list || []));
            } catch {
                if (active) setTrackList([]);
            }
        };

        void loadMapData();
        return () => {
            active = false;
        };
    }, [shipId, transportOrderDetailsShip]);

    useEffect(() => {
        if (!selectedShipInfo || !mapRef.current) return;
        mapRef.current.ship.render([selectedShipInfo]);
        mapRef.current.ship.selected(selectedShipInfo);
        mapRef.current.setCenter(selectedShipInfo.lon, selectedShipInfo.lat);
    }, [selectedShipInfo]);

    useEffect(() => {
        const map = mapRef.current;
        if (!map || !shipId || !trackList.length) return;
        void map.track.render(shipId, trackList, "#ff0000");
        return () => map.track.remove();
    }, [shipId, trackList]);

    if (!shipId) return null;

    return (
        <div aria-label="船舶物流地图" className="trade-order-logistics-map" data-testid="trade-order-logistics-map">
            <ZhMap
                ref={mapRef}
                className="trade-order-logistics-map__view"
                copyrightImgUrl={`${CDN_URL}map/tdt-copyright-h-w.svg`}
                layerType="satellite"
            />
            {firstDevice ? (
                <div className="trade-order-logistics-map__video-preview">
                    {captureUrl ? (
                        <span className="trade-order-logistics-map__video-image">
                            <img alt={firstDevice.name || "船舶设备抓拍"} src={captureUrl} />
                        </span>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}
